/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DealsService,
  type Deal,
  type DealDocument,
  type DealProposalStatus,
} from '@/services/requests/housien-services/DealsService';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import type { EditAttendanceFormData } from '../components/EditAttendanceDialog';

interface DealDetailIds {
  agencyId: string | undefined;
  dealId: string | undefined;
}

/**
 * The refusals this screen has its own wording for, by exception class.
 *
 * Keyed on the class name, never on the message: the detail is English,
 * written for a log, and matching it would group refusals by spelling.
 *
 * `DealPropertyRequiredError` is the one a broker hits by accident --
 * an offer on an attendance nobody has attached a property to -- and
 * the fix is one click away, so it is worth its own sentence. It used
 * to share `DealInvalidTransitionError`'s name, and that message named
 * the wrong cause.
 */
const REFUSAL_COPY_KEYS: Record<string, { title: string; message: string }> = {
  DealInvalidTransitionError: {
    title: 'detail.errors.invalidTransitionTitle',
    message: 'detail.errors.invalidTransitionMessage',
  },
  DealPropertyRequiredError: {
    title: 'detail.errors.propertyRequiredTitle',
    message: 'detail.errors.propertyRequiredMessage',
  },
  DealClientRequiredError: {
    title: 'detail.errors.clientRequiredTitle',
    message: 'detail.errors.clientRequiredMessage',
  },
};

/**
 * The stable exception class name carried by an API error, if any. Read
 * from `title` — never `detail`, which is for logs and is not localized.
 */
function errorTitleOf(error: unknown): string | undefined {
  const body = (error as { body?: { title?: string }; title?: string } | null);
  return body?.body?.title ?? body?.title;
}

/**
 * Localized wording for a refusal this screen recognizes.
 *
 * The generic classifier renders "an unexpected error occurred" for
 * these, but each is entirely predictable — an offer with no property,
 * or a move the state machine does not allow — and the broker deserves
 * to be told which one happened rather than watching a button do
 * nothing.
 *
 * @param error - The thrown value.
 * @param t - The `features/deals` translator.
 * @returns Report overrides, or undefined to keep the generic wording.
 */
function transitionRefusalCopy(
  error: unknown,
  t: (key: string) => string,
): { title: string; message: string } | undefined {
  const copy = REFUSAL_COPY_KEYS[errorTitleOf(error) ?? ''];
  if (!copy) return undefined;
  return { title: t(copy.title), message: t(copy.message) };
}

/**
 * Loads a deal plus its documents and exposes every lifecycle action,
 * each re-fetching the deal so the screen reflects what the backend
 * actually recorded (stage triggers can move more than the caller asked).
 *
 * Deliberately plain `useState` + `fetchDeal`, matching the rest of this
 * page — the screen has one owner and no cross-component cache to keep
 * in sync, so react-query would add a layer without a reader.
 *
 * Structure: data loading lives in {@link useDealData} and the lifecycle
 * actions in {@link useDealActions}; this hook only composes them and
 * shapes the public return. Splitting keeps each unit's cognitive
 * complexity within the gate without changing any behavior.
 *
 * @param ids - The agency and deal from the route.
 * @returns The loaded data, its load state, and the action callbacks.
 */
export function useDealDetail({ agencyId, dealId }: DealDetailIds) {
  const { t } = useTranslation('features/deals');
  const data = useDealData(agencyId, dealId, t);
  const actions = useDealActions(agencyId, dealId, data.fetchDeal, data.fetchDocuments, t);

  return {
    deal: data.deal,
    loading: data.loading,
    error: data.error,
    documents: data.documents,
    docsLoading: data.docsLoading,
    docsError: data.docsError,
    fetchDeal: data.fetchDeal,
    actions,
  };
}

type Translator = (key: string) => string;

/**
 * True when either route id is absent, so a fetch/action must no-op.
 *
 * Extracted so each callback guards with a single call rather than an
 * inline `||`, which keeps every callback's cognitive complexity low.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @returns True when the ids are not both present.
 */
function _missingIds(agencyId: string | undefined, dealId: string | undefined): boolean {
  return !agencyId || !dealId;
}

/**
 * The message to show when a load fails: the error's own text, or a
 * translated fallback for non-Error throws.
 *
 * @param e - The thrown value.
 * @param t - The `features/deals` translator.
 * @returns A human-readable load-error string.
 */
function _loadErrorMessage(e: unknown, t: Translator): string {
  return e instanceof Error ? e.message : t('loadError');
}

/**
 * Loads the deal and its documents, exposing their state and re-fetchers.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param t - The `features/deals` translator, for load-error copy.
 * @returns The deal, documents, their load/error flags, and re-fetchers.
 */
function useDealData(
  agencyId: string | undefined,
  dealId: string | undefined,
  t: Translator,
) {
  const dealState = useDealResource(agencyId, dealId, t);
  const docsState = useDocumentsResource(agencyId, dealId, t);

  useEffect(() => { void dealState.fetch(); }, [dealState.fetch]);
  useEffect(() => { void docsState.fetch(); }, [docsState.fetch]);

  return {
    deal: dealState.value,
    loading: dealState.loading,
    error: dealState.error,
    documents: docsState.value,
    docsLoading: docsState.loading,
    docsError: docsState.error,
    fetchDeal: dealState.fetch,
    fetchDocuments: docsState.fetch,
  };
}

/**
 * Loads the single deal, exposing its value, load flag, error, and fetcher.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param t - The `features/deals` translator, for load-error copy.
 * @returns The deal value, its load/error flags, and the re-fetcher.
 */
function useDealResource(
  agencyId: string | undefined,
  dealId: string | undefined,
  t: Translator,
) {
  const [value, setValue] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (_missingIds(agencyId, dealId)) return;
    setLoading(true);
    setError(null);
    try {
      setValue(await DealsService.getDeal(agencyId!, dealId!));
    } catch (e) {
      setError(_loadErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }, [agencyId, dealId, t]);

  return { value, loading, error, fetch };
}

/**
 * Loads the deal's documents, exposing the list, load flag, error, fetcher.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param t - The `features/deals` translator, for load-error copy.
 * @returns The documents, their load/error flags, and the re-fetcher.
 */
function useDocumentsResource(
  agencyId: string | undefined,
  dealId: string | undefined,
  t: Translator,
) {
  const [value, setValue] = useState<DealDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (_missingIds(agencyId, dealId)) return;
    setLoading(true);
    setError(null);
    try {
      setValue(await DealsService.getDocuments(agencyId!, dealId!));
    } catch (e) {
      setValue([]);
      setError(_loadErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }, [agencyId, dealId, t]);

  return { value, loading, error, fetch };
}

/**
 * Runs a service call against the current deal, then reloads it.
 *
 * A rejected call is surfaced as a toast rather than swallowed: the
 * backend refuses some transitions outright (a deal with no `deal_type`
 * cannot pass `proposal`, and there is no write path to set one), and
 * without this the button simply did nothing with no explanation of why.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param fetchDeal - Reloads the deal after a successful action.
 * @param t - The `features/deals` translator, for refusal copy.
 * @returns A `run(action)` callback returning whether the action stuck.
 */
function useDealRun(
  agencyId: string | undefined,
  dealId: string | undefined,
  fetchDeal: () => Promise<void>,
  t: Translator,
) {
  return useCallback(async (
    action: (agency: string, id: string) => Promise<unknown>,
  ): Promise<boolean> => {
    if (_missingIds(agencyId, dealId)) return false;
    try {
      await action(agencyId!, dealId!);
    } catch (e) {
      reportError(e, transitionRefusalCopy(e, t));
      return false;
    }
    await fetchDeal();
    return true;
  }, [agencyId, dealId, fetchDeal, t]);
}

/**
 * Every lifecycle and document action for the deal detail screen.
 *
 * Each mutation routes through {@link useDealRun} (reload-after-write with
 * toast-on-refusal); the document actions reload the document list instead.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param fetchDeal - Reloads the deal after a lifecycle action.
 * @param fetchDocuments - Reloads the document list after a document action.
 * @param t - The `features/deals` translator, forwarded to `run`.
 * @returns The frozen action map exposed to the screen.
 */
function useDealActions(
  agencyId: string | undefined,
  dealId: string | undefined,
  fetchDeal: () => Promise<void>,
  fetchDocuments: () => Promise<void>,
  t: Translator,
) {
  const run = useDealRun(agencyId, dealId, fetchDeal, t);

  // No `advance` and no `rollback` action on purpose: the stage follows
  // the recorded facts, so nothing in the UI is allowed to push it.
  // ``win`` and ``lose`` still reach the advance endpoint because those
  // two are human decisions, not derived facts.
  const pause = useCallback(() => run((a, d) => DealsService.pauseDeal(a, d)), [run]);
  const resume = useCallback(() => run((a, d) => DealsService.resumeDeal(a, d)), [run]);

  const lose = useCallback((reason: string, note: string) => run((a, d) => DealsService.advanceDeal(a, d, {
    target_stage: 'lost',
    lost_reason: reason,
    ...(note ? { lost_note: note } : {}),
  })), [run]);

  const recordProposal = useCallback(
    (value: number, body?: Parameters<typeof DealsService.recordProposal>[3]) =>
      run((a, d) => DealsService.recordProposal(a, d, value, body)), [run],
  );

  const recordAcceptedProposal = useCallback(
    (value: number, body?: Parameters<typeof DealsService.recordProposal>[3]) =>
      run((a, d) => _recordAndAccept(a, d, value, body)), [run],
  );

  const recordContact = useCallback(
    (note: string) => run((a, d) => DealsService.recordContact(a, d, note)), [run],
  );

  const counterProposal = useCallback(
    (proposalId: string, body: Parameters<typeof DealsService.counterProposal>[3]) =>
      run((a, d) => DealsService.counterProposal(a, d, proposalId, body)), [run],
  );

  const settleCondition = useCallback(
    (proposalId: string, conditionId: string, state: 'met' | 'failed') =>
      run((a, d) => DealsService.settleCondition(a, d, proposalId, conditionId, state)),
    [run],
  );

  const answerProposal = useCallback(
    (proposalId: string, status: DealProposalStatus) =>
      run((a, d) => DealsService.answerProposal(a, d, proposalId, status)), [run],
  );

  const deleteProposal = useCallback(
    (proposalId: string) =>
      run((a, d) => DealsService.deleteProposal(a, d, proposalId)), [run],
  );

  const setProperty = useCallback(
    (propertyId: string) => run((a, d) => DealsService.updateDeal(a, d, { property_id: propertyId })),
    [run],
  );

  const edit = useCallback(
    (fields: EditAttendanceFormData) => run((a, d) => DealsService.updateDeal(a, d, fields)),
    [run],
  );

  const docs = useDealDocumentActions(agencyId, dealId, fetchDocuments);

  return {
    pause, resume, lose,
    recordProposal, recordAcceptedProposal, answerProposal, deleteProposal,
    setProperty, edit,
    counterProposal, settleCondition, recordContact,
    uploadDocument: docs.uploadDocument,
    deleteDocument: docs.deleteDocument,
    renameDocument: docs.renameDocument,
  };
}

/**
 * Records the offer a win was agreed on, then accepts it.
 *
 * Two calls rather than one endpoint: accepting *is* the win, and the
 * backend has no other door to `closed_won` -- deliberately, since a win
 * with no offer behind it opens a Sale or a Lease with no number in it. A
 * close agreed on the phone is a real offer that was never written down,
 * so this writes it down and then answers it. The offer to accept is read
 * back from the response (its newest offer), never guessed.
 *
 * @param a - Agency id.
 * @param d - Deal id.
 * @param value - The agreed offer value to record.
 * @param body - Optional extra proposal fields.
 */
async function _recordAndAccept(
  a: string,
  d: string,
  value: number,
  body?: Parameters<typeof DealsService.recordProposal>[3],
): Promise<void> {
  const deal = await DealsService.recordProposal(a, d, value, body);
  const recorded = (deal.proposals ?? []).at(-1);
  if (!recorded) return;
  await DealsService.answerProposal(a, d, recorded.proposal_id, 'accepted');
}

/**
 * The three document actions, each reloading the document list on success.
 *
 * @param agencyId - The agency from the route (may be undefined).
 * @param dealId - The deal from the route (may be undefined).
 * @param fetchDocuments - Reloads the document list after each action.
 * @returns The upload, delete, and rename document callbacks.
 */
function useDealDocumentActions(
  agencyId: string | undefined,
  dealId: string | undefined,
  fetchDocuments: () => Promise<void>,
) {
  const uploadDocument = useCallback(async (file: File) => {
    if (_missingIds(agencyId, dealId)) return;
    const link = await DealsService.getUploadLink(agencyId!, dealId!, {
      filename: file.name,
      content_type: file.type,
    });
    if (link?.upload_url) {
      await fetch(link.upload_url, { method: 'PUT', body: file });
    }
    await fetchDocuments();
  }, [agencyId, dealId, fetchDocuments]);

  const deleteDocument = useCallback(async (documentId: string) => {
    if (_missingIds(agencyId, dealId)) return;
    await DealsService.deleteDocument(agencyId!, dealId!, documentId);
    await fetchDocuments();
  }, [agencyId, dealId, fetchDocuments]);

  const renameDocument = useCallback(async (documentId: string, filename: string) => {
    if (_missingIds(agencyId, dealId)) return;
    await DealsService.renameDocument(agencyId!, dealId!, documentId, filename);
    await fetchDocuments();
  }, [agencyId, dealId, fetchDocuments]);

  return { uploadDocument, deleteDocument, renameDocument };
}
